import { useCallback, useEffect, useState } from 'react';

import {
  PLATTFORM_NAME,
  type Kassenbuch,
  type KassenbuchArt,
  type KassenApi,
  type Plattformumsatz,
  type Tagesbericht,
} from '../api';
import { aktuelleRechte } from '../bediener';
import { formatiereEuro, parseEuro } from '../warenkorb';

const ARTEN: { id: KassenbuchArt; text: string }[] = [
  { id: 'einlage', text: 'Einlage (+)' },
  { id: 'entnahme', text: 'Entnahme (−)' },
  { id: 'zaehlung', text: 'Kassasturz (gezählt)' },
];

function artText(art: string): string {
  return ARTEN.find((a) => a.id === art)?.text ?? art;
}

function vorzeichen(cent: number): string {
  return `${cent > 0 ? '+' : ''}${formatiereEuro(cent)}`;
}

function heuteWien(): string {
  const teile = new Intl.DateTimeFormat('de-AT', {
    timeZone: 'Europe/Vienna',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const t = (typ: string): string => teile.find((x) => x.type === typ)?.value ?? '';
  return `${t('year')}-${t('month')}-${t('day')}`;
}

function zeit(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-AT', { timeZone: 'Europe/Vienna' });
}

/** Tagesbericht (Kassenabschluss): Umsatz nach Zahlungsart und Steuersatz, Storni, Belegzahlen. */
export function Bericht(props: { api: KassenApi; kassenId: string; bridgeDruck?: boolean }) {
  const { api, kassenId } = props;
  const [druckMeldung, setDruckMeldung] = useState<string | null>(null);
  const [datum, setDatum] = useState(heuteWien());
  const [bericht, setBericht] = useState<Tagesbericht | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [kassenbuch, setKassenbuch] = useState<Kassenbuch | null>(null);
  const [kbArt, setKbArt] = useState<KassenbuchArt>('einlage');
  /** Kassasturz gehört zum Tagesabschluss – nur mit Abschlussrecht (prüft auch der Server). */
  const darfZaehlen = aktuelleRechte(sessionStorage, kassenId).tagesabschluss;
  const [kbBetrag, setKbBetrag] = useState('');
  const [kbGrund, setKbGrund] = useState('');
  const [kbMeldung, setKbMeldung] = useState<string | null>(null);
  const [kbFehler, setKbFehler] = useState<string | null>(null);
  /** Online bezahlte Lieferdienst-Bestellungen des Tages; null ohne Modul. */
  const [plattform, setPlattform] = useState<Plattformumsatz[] | null>(null);
  const [plattformFehler, setPlattformFehler] = useState<string | null>(null);

  const laden = useCallback(
    async (tag: string) => {
      try {
        setBericht(await api.tagesbericht(kassenId, tag));
        setFehler(null);
      } catch (e: unknown) {
        setBericht(null);
        setFehler(e instanceof Error ? e.message : String(e));
      }
      try {
        setKassenbuch(await api.kassenbuch(kassenId, tag));
      } catch {
        setKassenbuch(null); // z. B. ohne Kassenbuch-Modul (501)
      }
      try {
        setPlattform((await api.plattformumsaetze(kassenId, tag)).umsaetze);
      } catch {
        setPlattform(null); // ohne Modul Lieferdienste (403/501)
      }
    },
    [api, kassenId],
  );

  async function plattformStornieren(u: Plattformumsatz): Promise<void> {
    const grund = window.prompt(
      `${PLATTFORM_NAME[u.plattform]}-Bestellung ${u.externeId} stornieren (von der Plattform storniert/erstattet)? Grund:`,
      '',
    );
    if (grund === null || grund.trim() === '') {
      return;
    }
    setPlattformFehler(null);
    try {
      await api.plattformumsatzStornieren(kassenId, u.id, grund.trim());
      await laden(datum);
    } catch (e: unknown) {
      setPlattformFehler(e instanceof Error ? e.message : String(e));
    }
  }

  async function bonDrucken(): Promise<void> {
    setDruckMeldung(null);
    try {
      const a = await api.tagesberichtDruckauftrag(kassenId, datum);
      setDruckMeldung(`Druckauftrag ${a.auftragId} an die Bridge übergeben`);
    } catch (e: unknown) {
      setDruckMeldung(e instanceof Error ? e.message : String(e));
    }
  }

  async function kbBuchen(): Promise<void> {
    setKbFehler(null);
    setKbMeldung(null);
    const cent = parseEuro(kbBetrag);
    if (cent === null) {
      setKbFehler('Betrag in Euro angeben, z. B. 50 oder 12,50');
      return;
    }
    try {
      const r = await api.kassenbuchBuchen(kassenId, {
        art: kbArt,
        betragCent: cent,
        ...(kbGrund.trim() === '' ? {} : { grund: kbGrund.trim() }),
      });
      const e = r.eintrag;
      setKbMeldung(
        e.art === 'zaehlung' && e.differenzCent !== null && e.sollCent !== null
          ? `Gezählt ${formatiereEuro(e.betragCent)} €, Soll ${formatiereEuro(e.sollCent)} €, Differenz ${vorzeichen(e.differenzCent)} €`
          : `${artText(e.art)} ${formatiereEuro(e.betragCent)} € gebucht – Bestand ${formatiereEuro(r.bestand.sollCent)} €`,
      );
      setKbBetrag('');
      setKbGrund('');
      await laden(datum);
    } catch (e: unknown) {
      setKbFehler(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    void laden(datum);
  }, [laden, datum]);

  return (
    <>
      <div className="karte kein-druck">
        <div className="zeile">
          <label>
            Tag
            <input
              type="date"
              value={datum}
              onChange={(e) => {
                setDatum(e.target.value);
              }}
            />
          </label>
          <button
            className="taste zweit"
            onClick={() => {
              void laden(datum);
            }}
          >
            Aktualisieren
          </button>
          <button
            className="taste"
            disabled={bericht === null}
            onClick={() => {
              window.print();
            }}
          >
            Drucken
          </button>
          {props.bridgeDruck === true && (
            <button
              className="taste zweit"
              disabled={bericht === null}
              onClick={() => void bonDrucken()}
            >
              Bon drucken (Bridge)
            </button>
          )}
        </div>
        {fehler !== null && <p className="fehler">{fehler}</p>}
        {druckMeldung !== null && <p className="hinweis">{druckMeldung}</p>}
      </div>
      {kassenbuch !== null && (
        <div className="karte kein-druck">
          <h3>Kassenbuch (Bargeld)</h3>
          <p>
            Rechnerischer Bestand: <strong>{formatiereEuro(kassenbuch.bestand.sollCent)} €</strong>
            {kassenbuch.bestand.letzteZaehlung !== null && (
              <>
                {' '}
                · letzte Zählung {formatiereEuro(kassenbuch.bestand.letzteZaehlung.istCent)} € (
                {zeit(kassenbuch.bestand.letzteZaehlung.zeitpunkt)})
              </>
            )}{' '}
            · seither Einlagen {formatiereEuro(kassenbuch.bestand.einlagenCent)} €, Entnahmen{' '}
            {formatiereEuro(kassenbuch.bestand.entnahmenCent)} €, Barumsatz{' '}
            {formatiereEuro(kassenbuch.bestand.barumsatzCent)} €
          </p>
          <div className="zeile">
            <label>
              Art
              <select
                value={kbArt}
                onChange={(e) => {
                  setKbArt(e.target.value as KassenbuchArt);
                }}
              >
                {ARTEN.map((a) => (
                  <option key={a.id} value={a.id} disabled={a.id === 'zaehlung' && !darfZaehlen}>
                    {a.text}
                    {a.id === 'zaehlung' && !darfZaehlen ? ' – nur mit Abschlussrecht' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {kbArt === 'zaehlung' ? 'Gezählt €' : 'Betrag €'}
              <input
                inputMode="decimal"
                value={kbBetrag}
                onChange={(e) => {
                  setKbBetrag(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    void kbBuchen();
                  }
                }}
              />
            </label>
            <label>
              Grund
              <input
                value={kbGrund}
                onChange={(e) => {
                  setKbGrund(e.target.value);
                }}
              />
            </label>
            <button className="taste" onClick={() => void kbBuchen()}>
              Buchen
            </button>
          </div>
          {kbMeldung !== null && <p className="ok">{kbMeldung}</p>}
          {kbFehler !== null && <p className="fehler">{kbFehler}</p>}
          {kassenbuch.eintraege.length > 0 && (
            <table className="positionen">
              <tbody>
                {kassenbuch.eintraege.map((e) => (
                  <tr key={e.id}>
                    <td>{zeit(e.erstelltAm)}</td>
                    <td>
                      {artText(e.art)}
                      {e.grund !== null && ` – ${e.grund}`}
                    </td>
                    <td className="betrag">
                      {formatiereEuro(e.betragCent)} €
                      {e.differenzCent !== null && ` (Δ ${vorzeichen(e.differenzCent)} €)`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {bericht !== null && (
        <div className="karte">
          <h2>
            Tagesbericht {bericht.kassenId} · {bericht.datum}
          </h2>
          <table className="positionen">
            <tbody>
              <tr>
                <th>Umsatz brutto (Standard + Storno)</th>
                <td className="betrag">{formatiereEuro(bericht.umsatz.bruttoCent)} €</td>
              </tr>
              <tr>
                <th>davon verkauft</th>
                <td className="betrag">{formatiereEuro(bericht.umsatz.verkauftCent)} €</td>
              </tr>
              <tr>
                <th>davon storniert</th>
                <td className="betrag">{formatiereEuro(bericht.umsatz.storniertCent)} €</td>
              </tr>
              <tr>
                <th>Bar ({bericht.umsatz.nachZahlungsart.bar.anzahl} Belege)</th>
                <td className="betrag">
                  {formatiereEuro(bericht.umsatz.nachZahlungsart.bar.bruttoCent)} €
                </td>
              </tr>
              <tr>
                <th>Karte ({bericht.umsatz.nachZahlungsart.karte.anzahl} Belege)</th>
                <td className="betrag">
                  {formatiereEuro(bericht.umsatz.nachZahlungsart.karte.bruttoCent)} €
                </td>
              </tr>
              {bericht.umsatz.nachZahlungsart.gutschein.anzahl > 0 && (
                <tr>
                  <th>Gutschein ({bericht.umsatz.nachZahlungsart.gutschein.anzahl} Belege)</th>
                  <td className="betrag">
                    {formatiereEuro(bericht.umsatz.nachZahlungsart.gutschein.bruttoCent)} €
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <h3>Nach Steuersatz</h3>
          <table className="positionen">
            <thead>
              <tr>
                <th>Satz</th>
                <th className="betrag">Netto</th>
                <th className="betrag">USt</th>
                <th className="betrag">Brutto</th>
              </tr>
            </thead>
            <tbody>
              {bericht.umsatz.nachSteuersatz.map((z) => (
                <tr key={z.steuersatz}>
                  <td>{z.steuersatz} %</td>
                  <td className="betrag">{formatiereEuro(z.nettoCent)}</td>
                  <td className="betrag">{formatiereEuro(z.ustCent)}</td>
                  <td className="betrag">{formatiereEuro(z.bruttoCent)}</td>
                </tr>
              ))}
              {bericht.umsatz.nachSteuersatz.length === 0 && (
                <tr>
                  <td colSpan={4}>Keine Umsätze an diesem Tag</td>
                </tr>
              )}
            </tbody>
          </table>
          {bericht.nachBediener.length > 0 && (
            <>
              <h3>Nach Bediener</h3>
              <table className="positionen">
                <tbody>
                  {bericht.nachBediener.map((k) => (
                    <tr key={k.bediener}>
                      <th>
                        {k.bediener === '' ? 'ohne Bediener' : k.bediener} ({k.standard} /{' '}
                        {k.storno} Storno)
                        {k.trinkgeldCent !== 0 &&
                          ` · Trinkgeld ${formatiereEuro(k.trinkgeldCent)} €`}
                      </th>
                      <td className="betrag">{formatiereEuro(k.bruttoCent)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          <h3>Belege</h3>
          <table className="positionen">
            <tbody>
              <tr>
                <th>Gesamt</th>
                <td className="betrag">{bericht.anzahl.gesamt}</td>
              </tr>
              <tr>
                <th>Standard / Storno</th>
                <td className="betrag">
                  {bericht.anzahl.standard} / {bericht.anzahl.storno}
                </td>
              </tr>
              {bericht.trinkgeldCent !== 0 && (
                <tr>
                  <th>Trinkgeld Karte (kein Umsatz, an Bediener)</th>
                  <td className="betrag">{formatiereEuro(bericht.trinkgeldCent)} €</td>
                </tr>
              )}
              <tr>
                <th>Training (nicht im Umsatz)</th>
                <td className="betrag">
                  {bericht.anzahl.training} · {formatiereEuro(bericht.trainingBruttoCent)} €
                </td>
              </tr>
              <tr>
                <th>Null-/Start-/Sonderbelege</th>
                <td className="betrag">{bericht.anzahl.nullbelege + bericht.anzahl.start}</td>
              </tr>
              <tr>
                <th>Ausfallbelege (Sicherheitseinrichtung ausgefallen)</th>
                <td className="betrag">{bericht.anzahl.ausfallbelege}</td>
              </tr>
              {(bericht.plattformen ?? []).map((p) => (
                <tr key={p.plattform}>
                  <th>Online bezahlt über {PLATTFORM_NAME[p.plattform]} (kein Barumsatz)</th>
                  <td className="betrag">
                    {p.anzahl}
                    {p.stornos > 0 && ` (${String(p.stornos)} storniert)`} ·{' '}
                    {formatiereEuro(p.bruttoCent)} €
                  </td>
                </tr>
              ))}
              {bericht.umsatzzaehler !== null && (
                <tr>
                  <th>Umsatzzähler Anfang → Ende</th>
                  <td className="betrag">
                    {formatiereEuro(bericht.umsatzzaehler.anfangCent)} €{' → '}
                    {formatiereEuro(bericht.umsatzzaehler.endeCent)} €
                  </td>
                </tr>
              )}
              {bericht.ersterBeleg !== null && bericht.letzterBeleg !== null && (
                <tr>
                  <th>Erster / letzter Beleg</th>
                  <td className="betrag">
                    {bericht.ersterBeleg.belegnummer} ({zeit(bericht.ersterBeleg.zeitpunkt)}) –{' '}
                    {bericht.letzterBeleg.belegnummer} ({zeit(bericht.letzterBeleg.zeitpunkt)})
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {plattform !== null && plattform.length > 0 && (
            <>
              <h3>Online bezahlte Bestellungen (ohne Registrierkassenbeleg)</h3>
              <table className="liste">
                <tbody>
                  {plattform.map((u) => (
                    <tr key={u.id}>
                      <td>{zeit(u.zeitpunkt)}</td>
                      <td>
                        {PLATTFORM_NAME[u.plattform]} {u.externeId}
                        {u.stornoVon !== null && ' – Storno'}
                      </td>
                      <td className="betrag">{formatiereEuro(u.summeCent)} €</td>
                      <td>
                        {u.stornoVon === null && !plattform.some((x) => x.stornoVon === u.id) && (
                          <button
                            type="button"
                            className="taste zweit"
                            onClick={() => void plattformStornieren(u)}
                          >
                            Stornieren
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {plattformFehler !== null && <p className="fehler">{plattformFehler}</p>}
            </>
          )}
          <h3>Bargeld</h3>
          <table className="positionen">
            <tbody>
              <tr>
                <th>Einlagen / Entnahmen</th>
                <td className="betrag">
                  {formatiereEuro(bericht.bargeld.einlagenCent)} € /{' '}
                  {formatiereEuro(bericht.bargeld.entnahmenCent)} €
                </td>
              </tr>
              {bericht.bargeld.zaehlungen.map((z) => (
                <tr key={z.zeitpunkt}>
                  <th>Kassasturz {zeit(z.zeitpunkt)} (Ist / Soll / Differenz)</th>
                  <td className="betrag">
                    {formatiereEuro(z.istCent)} € /{' '}
                    {z.sollCent === null ? '–' : `${formatiereEuro(z.sollCent)} €`} /{' '}
                    {z.differenzCent === null ? '–' : `${vorzeichen(z.differenzCent)} €`}
                  </td>
                </tr>
              ))}
              {bericht.bargeld.zaehlungen.length === 0 && (
                <tr>
                  <td colSpan={2}>Kein Kassasturz an diesem Tag</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
